import { db } from '../firebase';
import { collection, doc, getDocs, setDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';

export interface PointRule {
  id: string;
  category: string;
  points: string;
  description: string;
  criteria: string;
  frequency: string;
  enabled: boolean;
  trackable: boolean;
  order: number;
  weeklyCap?: number; // For "Weekly (capped)" frequency
  implementationNotes?: string; // Guidance on how to implement tracking
}

export interface PointCategory {
  id: string;
  name: string;
  enabled: boolean;
  order: number;
}

class PointService {
  private static instance: PointService;
  private pointRules: PointRule[] = [];
  private pointCategories: PointCategory[] = [];
  private initialized = false;
  private pointAwards: Map<string, { lastAwarded: Date; weeklyCount: number; dailyCount: number }> = new Map();

  private constructor() {}

  public static getInstance(): PointService {
    if (!PointService.instance) {
      PointService.instance = new PointService();
    }
    return PointService.instance;
  }

  // Initialize with default data if collections are empty
  public async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Check if we have any existing data
      const categoriesSnapshot = await getDocs(collection(db, 'pointCategories'));
      const rulesSnapshot = await getDocs(collection(db, 'pointRules'));

      // Only populate if BOTH collections are empty (prevent partial duplicates)
      if (categoriesSnapshot.empty && rulesSnapshot.empty) {
        console.log('Both collections empty, populating default data');
        await this.populateDefaultData();
      } else if (categoriesSnapshot.empty || rulesSnapshot.empty) {
        console.warn('One collection is empty but not both. This might indicate a data inconsistency.');
      }

      await this.loadData();
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing PointService:', error);
      throw error;
    }
  }

  // Populate with default data
  private async populateDefaultData(): Promise<void> {
    const defaultCategories: Omit<PointCategory, 'id'>[] = [
      { name: 'Practice & Learning Points', enabled: true, order: 1 },
      { name: 'Musical Progress Points', enabled: true, order: 2 },
      { name: 'Badge & Recognition Points', enabled: true, order: 3 },
      { name: 'Analytics & Progress Points', enabled: true, order: 4 },
      { name: 'Point Multipliers', enabled: true, order: 5 }
    ];

    const defaultRules: Omit<PointRule, 'id'>[] = [
      // Practice & Learning Points
      { category: 'Practice & Learning Points', points: '+10', description: 'Complete daily practice goal', criteria: 'Reach your set daily practice time goal', frequency: 'Daily', enabled: true, trackable: true, order: 1, implementationNotes: 'Tracked automatically in DailyPracticeGoalCard.tsx - compares daily practice time to goal' },
      { category: 'Practice & Learning Points', points: '+5', description: 'Practice for at least 50% of daily goal', criteria: 'Practice for at least half of your daily goal time', frequency: 'Daily', enabled: true, trackable: true, order: 2, implementationNotes: 'Tracked automatically in DailyPracticeGoalCard.tsx - calculates percentage of goal reached' },
      { category: 'Practice & Learning Points', points: '+5', description: 'Meet weekly practice frequency goal (1-2 sessions)', criteria: 'Complete 1-2 practice sessions in a week', frequency: 'Weekly', enabled: true, trackable: true, order: 3, implementationNotes: 'Tracked automatically in Analytics.tsx - counts practice sessions per week' },
      { category: 'Practice & Learning Points', points: '+10', description: 'Meet weekly practice frequency goal (3-4 sessions)', criteria: 'Complete 3-4 practice sessions in a week', frequency: 'Weekly', enabled: true, trackable: true, order: 4, implementationNotes: 'Tracked automatically in Analytics.tsx - counts practice sessions per week' },
      { category: 'Practice & Learning Points', points: '+20', description: 'Meet weekly practice frequency goal (5+ sessions)', criteria: 'Complete 5 or more practice sessions in a week', frequency: 'Weekly', enabled: true, trackable: true, order: 5, implementationNotes: 'Tracked automatically in Analytics.tsx - counts practice sessions per week' },
      { category: 'Practice & Learning Points', points: '+5', description: 'Complete guided practice session and meet daily goal', criteria: 'Finish a guided practice session and reach daily goal', frequency: 'Per session', enabled: true, trackable: true, order: 6, implementationNotes: 'Tracked automatically in PracticeTasks.tsx - after guided practice completion' },
      { category: 'Practice & Learning Points', points: '+3', description: 'Complete free practice session and meet daily goal', criteria: 'Finish a free practice session and reach daily goal', frequency: 'Per session', enabled: true, trackable: true, order: 7, implementationNotes: 'Tracked automatically in PracticeTasks.tsx - after free practice completion' },
      { category: 'Practice & Learning Points', points: '+5', description: 'Practice across all 3 categories in same week', criteria: 'Practice Technique, Repertoire, and Ensemble in one week', frequency: 'Weekly', enabled: true, trackable: true, order: 8, implementationNotes: 'Tracked automatically in PracticeTasks.tsx - checks practice session categories' },
      
      // Musical Progress Points
      { category: 'Musical Progress Points', points: '+10', description: 'Complete a lesson recording', criteria: 'Successfully record and submit a lesson', frequency: 'Per lesson', enabled: true, trackable: false, order: 9, implementationNotes: 'NOT TRACKABLE - Requires manual teacher verification of lesson completion' },
      { category: 'Musical Progress Points', points: '+5', description: 'Review lesson summary within 24 hours', criteria: 'View and read lesson summary within 24 hours of lesson', frequency: 'Per lesson', enabled: true, trackable: false, order: 10, implementationNotes: 'NOT TRACKABLE - Requires tracking when user views lesson summaries (could be added to LessonSummaries.tsx)' },
      { category: 'Musical Progress Points', points: '+5', description: 'Add at least 3 practice reminders from lesson notes', criteria: 'Create 3+ practice tips from lesson notes', frequency: 'Per lesson', enabled: true, trackable: false, order: 11, implementationNotes: 'NOT TRACKABLE - Requires counting practice reminders added from lesson notes (could be added to LessonSummaries.tsx)' },
      { category: 'Musical Progress Points', points: '+5', description: 'Add a new custom task', criteria: 'Create a new custom practice task', frequency: 'Weekly (capped)', enabled: true, trackable: true, order: 12, weeklyCap: 3, implementationNotes: 'Tracked automatically in PracticeTasks.tsx - when custom tasks are created' },
      { category: 'Musical Progress Points', points: '+5', description: 'Complete a custom task 5 times', criteria: 'Complete the same custom task 5 times', frequency: 'Per task', enabled: true, trackable: false, order: 13, implementationNotes: 'NOT TRACKABLE - Requires tracking individual task completion counts (could be added to PracticeTasks.tsx)' },
      
      // Badge & Recognition Points
      { category: 'Badge & Recognition Points', points: '+5', description: 'Earn any automatic badge', criteria: 'Achieve requirements for any automatic badge', frequency: 'Per badge', enabled: true, trackable: true, order: 14, implementationNotes: 'Tracked automatically in Awards.tsx - when badges are awarded' },
      { category: 'Badge & Recognition Points', points: '+5', description: 'Earn a teacher-assigned badge', criteria: 'Receive a badge assigned by your teacher', frequency: 'Per badge', enabled: true, trackable: true, order: 15, implementationNotes: 'Tracked automatically in TeacherAwards.tsx - when teacher assigns badges' },
      { category: 'Badge & Recognition Points', points: '+10', description: 'Earn a milestone badge', criteria: 'Achieve milestone badges (100 hours, etc.)', frequency: 'Per badge', enabled: true, trackable: true, order: 16, implementationNotes: 'Tracked automatically in Awards.tsx - when milestone badges are awarded' },
      
      // Analytics & Progress Points
      { category: 'Analytics & Progress Points', points: '+5', description: 'Check analytics page', criteria: 'Visit and view the analytics dashboard', frequency: 'Weekly (capped)', enabled: true, trackable: false, order: 17, weeklyCap: 1, implementationNotes: 'NOT TRACKABLE - Requires tracking page visits (could be added to Analytics.tsx)' },
      { category: 'Analytics & Progress Points', points: '+15', description: 'Set new personal record for weekly practice time', criteria: 'Practice more hours in a week than ever before', frequency: 'Per record', enabled: true, trackable: false, order: 18, implementationNotes: 'NOT TRACKABLE - Requires comparing current week to historical data (could be added to Analytics.tsx)' },
      { category: 'Analytics & Progress Points', points: '+5', description: 'Beat previous week\'s practice time', criteria: 'Practice more than the previous week', frequency: 'Weekly', enabled: true, trackable: false, order: 19, implementationNotes: 'NOT TRACKABLE - Requires comparing current week to previous week (could be added to Analytics.tsx)' },
      
      // Point Multipliers
      { category: 'Point Multipliers', points: '1.2x', description: '7-day practice streak', criteria: 'Practice for 7 consecutive days', frequency: 'Ongoing', enabled: true, trackable: true, order: 20, implementationNotes: 'Tracked automatically in DailyPracticeGoalCard.tsx - streak calculation' },
      { category: 'Point Multipliers', points: '1.5x', description: '30-day practice streak', criteria: 'Practice for 30 consecutive days', frequency: 'Ongoing', enabled: true, trackable: true, order: 21, implementationNotes: 'Tracked automatically in DailyPracticeGoalCard.tsx - streak calculation' },
      { category: 'Point Multipliers', points: '2.0x', description: '100-day practice streak', criteria: 'Practice for 100 consecutive days', frequency: 'Ongoing', enabled: true, trackable: true, order: 22, implementationNotes: 'Tracked automatically in DailyPracticeGoalCard.tsx - streak calculation' }
    ];

    // Create categories
    for (const category of defaultCategories) {
      const categoryRef = doc(collection(db, 'pointCategories'));
      await setDoc(categoryRef, category);
    }

    // Create rules
    for (const rule of defaultRules) {
      const ruleRef = doc(collection(db, 'pointRules'));
      await setDoc(ruleRef, rule);
    }

    console.log('Default point system data populated');
  }

  // Load data from Firestore
  public async loadData(): Promise<void> {
    try {
      // Load categories
      const categoriesSnapshot = await getDocs(query(collection(db, 'pointCategories'), orderBy('order')));
      this.pointCategories = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PointCategory));

      // Load rules
      const rulesSnapshot = await getDocs(query(collection(db, 'pointRules'), orderBy('order')));
      this.pointRules = rulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PointRule));

      // Remove duplicates based on name for categories
      this.pointCategories = this.pointCategories.filter((category, index, self) => 
        index === self.findIndex(c => c.name === category.name)
      );

      // Remove duplicates based on description for rules
      this.pointRules = this.pointRules.filter((rule, index, self) => 
        index === self.findIndex(r => r.description === rule.description)
      );

      console.log(`Loaded ${this.pointCategories.length} categories and ${this.pointRules.length} rules`);
    } catch (error) {
      console.error('Error loading point data:', error);
      throw error;
    }
  }

  // Get all categories
  public getCategories(): PointCategory[] {
    return [...this.pointCategories];
  }

  // Get all rules
  public getRules(): PointRule[] {
    return [...this.pointRules];
  }

  // Get rules by category
  public getRulesByCategory(categoryName: string): PointRule[] {
    return this.pointRules.filter(rule => rule.category === categoryName);
  }

  // Get enabled rules only
  public getEnabledRules(): PointRule[] {
    return this.pointRules.filter(rule => rule.enabled);
  }

  // Get trackable rules only
  public getTrackableRules(): PointRule[] {
    return this.pointRules.filter(rule => rule.trackable && rule.enabled);
  }

  // Get point value for a specific action
  public getPointValue(description: string): number {
    const rule = this.pointRules.find(rule => 
      rule.description === description && rule.enabled
    );
    
    if (!rule) return 0;
    
    // Handle multipliers
    if (rule.points.includes('x')) {
      return parseFloat(rule.points.replace('x', ''));
    }
    
    // Handle regular points
    return parseInt(rule.points.replace('+', '')) || 0;
  }

  // Update a rule
  public async updateRule(ruleId: string, updates: Partial<PointRule>): Promise<void> {
    try {
      const ruleRef = doc(db, 'pointRules', ruleId);
      await updateDoc(ruleRef, updates);
      
      // Update local cache
      const ruleIndex = this.pointRules.findIndex(rule => rule.id === ruleId);
      if (ruleIndex !== -1) {
        this.pointRules[ruleIndex] = { ...this.pointRules[ruleIndex], ...updates };
      }
    } catch (error) {
      console.error('Error updating rule:', error);
      throw error;
    }
  }

  // Add a new rule
  public async addRule(rule: Omit<PointRule, 'id'>): Promise<string> {
    try {
      const ruleRef = doc(collection(db, 'pointRules'));
      await setDoc(ruleRef, rule);
      
      // Add to local cache
      const newRule = { id: ruleRef.id, ...rule };
      this.pointRules.push(newRule);
      
      return ruleRef.id;
    } catch (error) {
      console.error('Error adding rule:', error);
      throw error;
    }
  }

  // Delete a rule
  public async deleteRule(ruleId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'pointRules', ruleId));
      
      // Remove from local cache
      this.pointRules = this.pointRules.filter(rule => rule.id !== ruleId);
    } catch (error) {
      console.error('Error deleting rule:', error);
      throw error;
    }
  }

  // Update a category
  public async updateCategory(categoryId: string, updates: Partial<PointCategory>): Promise<void> {
    try {
      const categoryRef = doc(db, 'pointCategories', categoryId);
      await updateDoc(categoryRef, updates);
      
      // Update local cache
      const categoryIndex = this.pointCategories.findIndex(cat => cat.id === categoryId);
      if (categoryIndex !== -1) {
        this.pointCategories[categoryIndex] = { ...this.pointCategories[categoryIndex], ...updates };
      }
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  // Add a new category
  public async addCategory(category: Omit<PointCategory, 'id'>): Promise<string> {
    try {
      const categoryRef = doc(collection(db, 'pointCategories'));
      await setDoc(categoryRef, category);
      
      // Add to local cache
      const newCategory = { id: categoryRef.id, ...category };
      this.pointCategories.push(newCategory);
      
      return categoryRef.id;
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  }

  // Delete a category
  public async deleteCategory(categoryId: string): Promise<void> {
    try {
      // First delete all rules in this category
      const rulesInCategory = this.pointRules.filter(rule => rule.category === categoryId);
      for (const rule of rulesInCategory) {
        await this.deleteRule(rule.id);
      }
      
      // Then delete the category
      await deleteDoc(doc(db, 'pointCategories', categoryId));
      
      // Remove from local cache
      this.pointCategories = this.pointCategories.filter(cat => cat.id !== categoryId);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  // Toggle rule enabled/disabled
  public async toggleRule(ruleId: string): Promise<void> {
    const rule = this.pointRules.find(r => r.id === ruleId);
    if (rule) {
      await this.updateRule(ruleId, { enabled: !rule.enabled });
    }
  }

  // Toggle category enabled/disabled
  public async toggleCategory(categoryId: string): Promise<void> {
    const category = this.pointCategories.find(c => c.id === categoryId);
    if (category) {
      await this.updateCategory(categoryId, { enabled: !category.enabled });
    }
  }

  // Check if a point rule can be awarded based on frequency
  public canAwardPoints(ruleId: string, userId: string): boolean {
    const rule = this.pointRules.find(r => r.id === ruleId);
    if (!rule || !rule.enabled) return false;

    const awardKey = `${userId}_${ruleId}`;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Go back to Sunday

    const awardData = this.pointAwards.get(awardKey) || {
      lastAwarded: new Date(0),
      weeklyCount: 0,
      dailyCount: 0
    };

    // Reset weekly count if it's a new week
    if (awardData.lastAwarded < startOfWeek) {
      awardData.weeklyCount = 0;
      awardData.dailyCount = 0;
    }

    // Check frequency rules
    switch (rule.frequency) {
      case 'Daily':
        return awardData.lastAwarded < today;
      
      case 'Weekly':
        return awardData.lastAwarded < startOfWeek;
      
      case 'Weekly (capped)':
        if (awardData.lastAwarded < startOfWeek) {
          awardData.weeklyCount = 0; // Reset for new week
        }
        return awardData.weeklyCount < (rule.weeklyCap || 1);
      
      case 'Per session':
      case 'Per lesson':
      case 'Per task':
      case 'Per badge':
      case 'Per record':
        return true; // Always allow these
      
      case 'Ongoing':
        return true; // Multipliers are always active
      
      default:
        return true;
    }
  }

  // Record that points were awarded for a rule
  public recordPointAward(ruleId: string, userId: string): void {
    const awardKey = `${userId}_${ruleId}`;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const awardData = this.pointAwards.get(awardKey) || {
      lastAwarded: new Date(0),
      weeklyCount: 0,
      dailyCount: 0
    };

    // Reset weekly count if it's a new week
    if (awardData.lastAwarded < startOfWeek) {
      awardData.weeklyCount = 0;
      awardData.dailyCount = 0;
    }

    awardData.lastAwarded = now;
    awardData.dailyCount++;
    awardData.weeklyCount++;

    this.pointAwards.set(awardKey, awardData);
  }

  // Get award status for a rule
  public getAwardStatus(ruleId: string, userId: string): {
    canAward: boolean;
    lastAwarded: Date | null;
    dailyCount: number;
    weeklyCount: number;
    weeklyCap?: number;
  } {
    const rule = this.pointRules.find(r => r.id === ruleId);
    const awardKey = `${userId}_${ruleId}`;
    const awardData = this.pointAwards.get(awardKey);

    return {
      canAward: this.canAwardPoints(ruleId, userId),
      lastAwarded: awardData?.lastAwarded || null,
      dailyCount: awardData?.dailyCount || 0,
      weeklyCount: awardData?.weeklyCount || 0,
      weeklyCap: rule?.weeklyCap
    };
  }

  // Clear award tracking (useful for testing or admin reset)
  public clearAwardTracking(userId?: string): void {
    if (userId) {
      // Clear for specific user
      const keysToDelete: string[] = [];
      this.pointAwards.forEach((value, key) => {
        if (key.startsWith(`${userId}_`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.pointAwards.delete(key));
    } else {
      // Clear all
      this.pointAwards.clear();
    }
  }

  // Clear all point system data and repopulate (admin function)
  public async resetToDefaults(): Promise<void> {
    try {
      console.log('Resetting point system to defaults...');
      
      // Clear existing data
      const categoriesSnapshot = await getDocs(collection(db, 'pointCategories'));
      const rulesSnapshot = await getDocs(collection(db, 'pointRules'));

      // Delete all categories
      for (const doc of categoriesSnapshot.docs) {
        await deleteDoc(doc.ref);
      }

      // Delete all rules
      for (const doc of rulesSnapshot.docs) {
        await deleteDoc(doc.ref);
      }

      // Clear local cache
      this.pointCategories = [];
      this.pointRules = [];
      this.initialized = false;

      // Repopulate with defaults
      await this.populateDefaultData();
      await this.loadData();
      this.initialized = true;

      console.log('Point system reset to defaults completed');
    } catch (error) {
      console.error('Error resetting point system:', error);
      throw error;
    }
  }
}

export default PointService;
